// =============================================================================
// One month of the calendar: weekday header, six week rows with the day
// numbers, birthday pills, event chips (FadeClip) and the multi-day bar
// overlay. Rendered three times by the carousel in Calendar.js (previous /
// current / next month), so it is a pure function of its props.
//
// `compact` is the phone density: no week-number column, 10px chips, 100px
// rows. Calendar.js sets it from the grid's own width, not the viewport —
// beside the day column on an iPad the grid is phone-wide even though the
// screen is not — which is why the sizes below are switched in JS instead
// of with responsive classes: the multi-day bars are positioned with inline
// pixel offsets that must match the chip height exactly.
// =============================================================================

import React from 'react';
import { Gift, CheckSquare } from 'lucide-react';
import { format, isSameMonth, isSameDay, isToday, getWeek } from 'date-fns';
import { priorityEmoji } from '../../lib/priorities';
import { useTranslation } from '../../lib/i18n';
import FadeClip from '../FadeClip';
import { getWeeksForMonth, eventsForDay, multiDayBarsForWeek } from './utils';

const MonthGrid = ({
  baseDate,
  events,
  compact,
  weekdayLabels,
  getCategory,
  selectedDate,
  popToday,
  onDayClick
}) => {
  const { t } = useTranslation();
  // Multi-day-bar absolute offsets (inline styles) and the optional
  // week-number column stay in sync via the same flag.
  const kwColWidth = compact ? 0 : 50;
  const chipText = compact ? 'text-[10px]' : 'text-xs';
  const baseWeeks = getWeeksForMonth(baseDate);
  return (
    <div className="w-full flex-shrink-0">
      {/* Weekday header */}
      <div
        className="grid border-b border-gray-200 bg-gray-50"
        style={{
          gridTemplateColumns: compact
            ? 'repeat(7,minmax(0,1fr))'
            : '50px repeat(7,minmax(0,1fr))'
        }}
      >
        {!compact && (
          <div className="p-2 text-xs font-medium text-gray-400 text-center">{t('calendar.weekShort')}</div>
        )}
        {weekdayLabels.map((lbl, i) => (
          <div
            key={lbl}
            className={`${compact ? 'p-1.5 text-[10px]' : 'p-2 text-xs'} font-medium text-center uppercase tracking-wider ${
              i >= 5 ? 'text-red-500' : 'text-gray-600'
            }`}
          >
            {lbl}
          </div>
        ))}
      </div>

      {/* Weeks */}
      <div className="divide-y divide-gray-200">
        {baseWeeks.map((week, wIdx) => {
          const multiDayBars = multiDayBarsForWeek(events, week);
          const multiDayIds = new Set(multiDayBars.map((b) => b.event.id));
          const barRowCount = multiDayBars.reduce((m, b) => Math.max(m, b.row + 1), 0);
          const BAR_HEIGHT = compact ? 16 : 20; // px — match chip height when compact
          const BAR_TOP_OFFSET = compact ? 26 : 32; // px below day number

          return (
            <div
              key={wIdx}
              className={`grid ${compact ? 'min-h-[100px]' : 'min-h-[120px]'} relative`}
              style={{
                gridTemplateColumns: compact
                  ? 'repeat(7,minmax(0,1fr))'
                  : '50px repeat(7,minmax(0,1fr))'
              }}
            >
              {!compact && (
                <div className="flex items-start justify-center pt-2 bg-gray-50 text-xs font-medium text-gray-400">
                  {getWeek(week[0], { weekStartsOn: 1, firstWeekContainsDate: 4 })}
                </div>
              )}

              {week.map((day, dayIdx) => {
                const inMonth = isSameMonth(day, baseDate);
                const today = isToday(day);
                const weekend = day.getDay() === 0 || day.getDay() === 6;
                const allDayEvents = eventsForDay(events, day).filter((ev) => !multiDayIds.has(ev.id));
                // Per-day reserved space: only the bars that actually cross
                // THIS column's index push content down. Otherwise every
                // day in the week would inherit gaps from bars on a
                // different day (e.g. the 23rd or 30th).
                const maxBarRowHere = multiDayBars.reduce(
                  (m, b) =>
                    dayIdx >= b.startCol && dayIdx <= b.endCol
                      ? Math.max(m, b.row + 1)
                      : m,
                  0
                );
                const dayReservedSpace = maxBarRowHere * BAR_HEIGHT;
                const birthdayEventsForDay = allDayEvents.filter((ev) => ev.isBirthday);
                const dayEvents = allDayEvents.filter((ev) => !ev.isBirthday);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const firstBirthday = birthdayEventsForDay[0];
                const firstBirthdayCat = firstBirthday ? getCategory(firstBirthday.categoryId) : null;

                return (
                  <div
                    key={day.toISOString()}
                    onClick={() => onDayClick(day)}
                    className={`border-l border-gray-200 ${compact ? 'p-1' : 'p-1.5'} cursor-pointer transition-colors group min-w-0 overflow-hidden ${
                      inMonth ? 'bg-white' : 'bg-gray-50/50 opacity-40'
                    } ${isSelected ? 'ring-2 ring-primary-500 ring-inset' : ''} hover:bg-gray-50`}
                  >
                    <div className="flex items-center gap-1 mb-1 min-w-0">
                      <span
                        className={`flex-shrink-0 inline-flex items-center justify-center ${compact ? 'w-5 h-5 text-[11px]' : 'w-6 h-6 text-xs'} font-medium rounded-full ${
                          today && popToday ? 'today-pop' : ''
                        } ${
                          today
                            ? 'bg-primary-600 text-white'
                            : !inMonth
                            ? 'text-gray-300'
                            : weekend
                            ? 'text-red-500'
                            : 'text-gray-700'
                        }`}
                      >
                        {format(day, 'd')}
                      </span>

                      {/* Birthday pill (inline next to day number) */}
                      {firstBirthday && firstBirthdayCat && (
                        <div
                          className="flex-1 min-w-0 text-xs px-1.5 py-0.5 rounded font-semibold flex items-center gap-1"
                          style={{
                            backgroundColor: `${firstBirthdayCat.color}22`,
                            color: firstBirthdayCat.color
                          }}
                          title={birthdayEventsForDay.map((b) => b.title).join(', ')}
                        >
                          <Gift size={12} className="flex-shrink-0" />
                          <span className="truncate">
                            {firstBirthday.title}
                            {birthdayEventsForDay.length > 1 && ` +${birthdayEventsForDay.length - 1}`}
                          </span>
                        </div>
                      )}

                    </div>

                    {/* Reserved space for multi-day bars that cross THIS day */}
                    {dayReservedSpace > 0 && (
                      <div style={{ height: dayReservedSpace }} aria-hidden="true" />
                    )}

                    {/* Events (single-day + timed multi-day) */}
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 4).map((ev) => {
                        const cat = getCategory(ev.categoryId);
                        const isAllDay = ev.allDay !== false;
                        if (ev.isTodo) {
                          // Todo chip — birthday-pill styling, but with the
                          // todo symbol; crossed off once the task is done.
                          return (
                            <div
                              key={ev.id}
                              className={`${chipText} ${compact ? 'pl-1' : 'pl-1.5'} pr-0.5 py-0.5 rounded font-medium leading-tight flex items-center gap-1 overflow-hidden`}
                              style={{ backgroundColor: `${cat.color}22`, color: cat.color }}
                              title={ev.startTime ? `${ev.startTime} ${ev.title}` : ev.title}
                            >
                              <CheckSquare size={10} className="flex-shrink-0" />
                              <span
                                className={`truncate flex-1 min-w-0 ${
                                  ev.done ? 'line-through opacity-60' : ''
                                }`}
                              >
                                {ev.title}
                              </span>
                              {priorityEmoji(ev.priority) && (
                                <span className="flex-shrink-0 leading-none pr-0.5">
                                  {priorityEmoji(ev.priority)}
                                </span>
                              )}
                            </div>
                          );
                        }
                        return (
                          <div
                            key={ev.id}
                            className={`${chipText} ${compact ? 'px-1' : 'px-1.5'} py-0.5 rounded font-medium cursor-pointer hover:opacity-80 leading-tight overflow-hidden`}
                            style={
                              isAllDay
                                ? { backgroundColor: cat.color, color: '#fff' }
                                : { backgroundColor: `${cat.color}22`, color: cat.color }
                            }
                            title={ev.startTime ? `${ev.startTime} ${ev.title}` : ev.title}
                          >
                            <FadeClip>{ev.title}</FadeClip>
                          </div>
                        );
                      })}
                      {dayEvents.length > 4 && (
                        <div className="text-xs text-gray-500 font-medium px-1">
                          {t('calendar.moreCount', { n: dayEvents.length - 4 })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Multi-day bars overlay */}
              {multiDayBars.length > 0 && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: kwColWidth,
                    right: 0,
                    top: BAR_TOP_OFFSET,
                    height: barRowCount * BAR_HEIGHT
                  }}
                >
                  {multiDayBars.map((bar) => {
                    const cat = getCategory(bar.event.categoryId);
                    const widthPct = ((bar.endCol - bar.startCol + 1) / 7) * 100;
                    const leftPct = (bar.startCol / 7) * 100;
                    return (
                      <div
                        key={bar.event.id}
                        className={`absolute ${chipText} font-medium truncate ${compact ? 'px-1' : 'px-2'} leading-tight pointer-events-none flex items-center justify-center ${
                          bar.continuesLeft ? '' : 'rounded-l'
                        } ${bar.continuesRight ? '' : 'rounded-r'}`}
                        style={{
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                          top: bar.row * BAR_HEIGHT,
                          height: BAR_HEIGHT - 2,
                          backgroundColor: cat.color,
                          color: '#fff'
                        }}
                        title={bar.event.title}
                      >
                        {bar.event.title}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthGrid;
